import { useEffect, useMemo, useState } from "react";
import { api, fmtIsoDateOnly } from "@/lib/api";
import { History, Trash2 } from "lucide-react";
import AffectedCalculationsDialog from "@/components/AffectedCalculationsDialog";
import { toast } from "sonner";

// Rate History — read-only timeline + delete control mounted inside the
// Merchant / Gateway edit modals. New rate versions are created ONLY
// through the entity's Edit form (which surfaces RateChangeDialog on a
// scalar rate change). This tab does NOT have an "Add new rate" button —
// admins should never need a separate add path.
//
// Props:
//   `scope`         "merchant_mid" | "gateway_lob" | "partner_lob"
//                   (iter31 — partner_lob re-added; keyed by
//                    partner_id + gateway + lob to match rate_cards[])
//   `keyFields`     identifier subset (e.g. {merchant_id} for
//                   merchant_mid; {gateway, lob} for gateway_lob;
//                   {partner_id, gateway, lob} for partner_lob)
//   `currentRate`   currently-active scalar rate (decimal, e.g. 0.015)
//                   used in the helper banner only.
//
// Backfill rows (created_by_email == "system:backfill") are read-only —
// the trash control is replaced with a "Read-only" label.
export default function RateHistoryTab({ scope, keyFields, currentRate }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  // PR-A.5 / iter30 — destructive delete now opens a richer dialog that
  // previews which calculations reference this version (closed-period
  // warning surface). `pendingDelete` holds the row whose Trash was clicked
  // until the user confirms or cancels the dialog.
  const [pendingDelete, setPendingDelete] = useState(null);

  const keyComplete = useMemo(() => {
    if (scope === "merchant_mid") return !!keyFields.merchant_id;
    if (scope === "gateway_lob") return !!(keyFields.gateway && keyFields.lob);
    if (scope === "partner_lob") return !!(keyFields.partner_id && keyFields.gateway && keyFields.lob);
    if (scope === "partner_revenue_share") return !!keyFields.partner_id;
    return false;
  }, [scope, keyFields]);

  const load = async () => {
    if (!keyComplete) { setRows([]); return; }
    setLoading(true);
    try {
      const params = { scope, ...keyFields };
      const { data } = await api.get("/rate-versions", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rate history");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, JSON.stringify(keyFields), keyComplete]);

  const fmtDate = fmtIsoDateOnly;
  const fmtRate = (v) => {
    const n = Number(v || 0);
    return `${(n * 100).toFixed(4).replace(/\.?0+$/, "")}%`;
  };

  const remove = (row) => {
    if (row.created_by_email === "system:backfill") return;
    setPendingDelete(row);
  };

  const performDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/rate-versions/${pendingDelete.id}`);
      toast.success("Rate version deleted");
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!keyComplete) {
    return (
      <div className="p-4 text-[12.5px] text-[var(--muted)] bg-[var(--surface-2)] border border-[var(--line)] rounded-sm" data-testid="rh-incomplete-key">
        {scope === "merchant_mid" && "Save the merchant first — rate history is keyed by Merchant ID."}
        {scope === "gateway_lob" && "Pick both Gateway and LOB to view rate history."}
        {scope === "partner_lob" && "Pick a Gateway × LOB rate card to view its rate history."}
        {scope === "partner_revenue_share" && "Save the partner first — revenue share history is keyed by Partner ID."}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="rate-history-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[12px] text-[var(--muted)]">
          <History size={13} />
          <span>{rows.length} version{rows.length === 1 ? "" : "s"} on record</span>
          {currentRate != null && (
            <span className="ml-2 num">· current scalar: <b className="text-[var(--ink)]">{fmtRate(currentRate)}</b></span>
          )}
        </div>
        <span className="text-[10.5px] text-[var(--muted)] italic">
          Add new rates by editing the entity's rate on the Details tab.
        </span>
      </div>

      <div className="border border-[var(--line)] rounded-sm overflow-hidden">
        <table className="table-data w-full" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "16%" }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "12%" }} />
            <col />
            <col style={{ width: "20%" }} />
            <col style={{ width: "56px" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Effective from</th>
              <th>Effective to</th>
              <th className="text-right">Rate</th>
              <th>Reason</th>
              <th>By</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center text-[var(--muted)] py-5">Loading…</td></tr>}
            {!loading && !rows.length && <tr><td colSpan={6} className="text-center text-[var(--muted)] py-5">No history yet — change the rate on the Details tab to create the first version.</td></tr>}
            {!loading && rows.map((r) => {
              const active = !r.effective_to;
              const isBackfill = r.created_by_email === "system:backfill";
              return (
                <tr key={r.id} data-testid={`rh-row-${r.id}`} className={active ? "bg-emerald-50/40" : ""}>
                  <td className="num text-[12px]">{fmtDate(r.effective_from)}</td>
                  <td className="num text-[12px]">{active
                    ? <span className="text-emerald-700 font-semibold uppercase tracking-wider text-[10.5px]">Active</span>
                    : fmtDate(r.effective_to)}</td>
                  <td className="num text-right text-[12px] font-semibold text-[var(--ink)]">{fmtRate(r.rate_value)}</td>
                  <td className="text-[11.5px] text-[var(--muted)] truncate" title={r.reason || ""}>{r.reason || "—"}</td>
                  <td className="text-[10.5px] text-[var(--muted)] truncate" title={r.created_by_email || ""}>{r.created_by_email || "—"}</td>
                  <td className="text-center" style={{ width: "56px" }}>
                    {isBackfill ? (
                      <span
                        title="Legacy backfill row — read-only"
                        className="inline-block text-[9px] uppercase tracking-wider text-[var(--muted)] px-1.5 py-0.5 bg-[var(--surface-2)] rounded-sm"
                      >
                        Locked
                      </span>
                    ) : (
                      <button
                        onClick={() => remove(r)}
                        data-testid={`rh-delete-${r.id}`}
                        className="inline-flex items-center justify-center p-1.5 hover:bg-red-50 text-[var(--negative)] rounded-sm transition-colors"
                        title="Delete this rate version"
                        aria-label="Delete rate version"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AffectedCalculationsDialog
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={performDelete}
        versionId={pendingDelete?.id}
        rateLabel={pendingDelete ? fmtRate(pendingDelete.rate_value) : ""}
        effectiveFromLabel={pendingDelete ? fmtDate(pendingDelete.effective_from) : ""}
      />
    </div>
  );
}
